import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, colors, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Button, CircularProgress } from '@mui/material';

import Auth, { API_URL, generateCSRFToken } from "../../utils/auth";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from 'react-toastify';
import CircleModal from "./Integrations/CircleIntegrations/CircleModal";
import CircleManagement from "./Integrations/CircleIntegrations/CircleManagement";
import SlackModal from "./Integrations/SlackIntegrations/SlackModal";
import HighLevelModal from "./Integrations/HighLevelIntegrations/HighLevelModal";
import ZoomManagement from "./Integrations/ZoomIntegrations/ZoomManagement";
import HighLevelContactSync from "./Integrations/HighLevelIntegrations/HighLevelContactSync";
import HubspotModal from "./Integrations/HubspotIntegrations/HubspotModal";
import HubspotContactSync from "./Integrations/HubspotIntegrations/HubspotContactSync";
import "./IntegrationsMain.css";

const ZAPIER_WEBINTENT_BASE_URL = "https://zapier.com/webintent/create-zap?";

const IntegrationsMain = ({ integrationStatus }) => {
  const [connectionStatus, setConnectionStatus] = useState(integrationStatus || {});
  const [error, setError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const navigate = useNavigate();
  const [slackModalOpen, setSlackModalOpen] = useState(false);
  const [circleModalOpen, setCircleModalOpen] = useState(false);
  const [circleManagementOpen, setCircleManagementOpen] = useState(false);
  const [highLevelModalOpen, setHighLevelModalOpen] = useState(false);
  const [zoomManagementOpen, setZoomManagementOpen] = useState(false);
  const [highLevelContactSyncModealOpen, setHighLevelContactSyncModalOpen] = useState(false);
  const [hubspotModalOpen, setHubspotModalOpen] = useState(false);
  const [hubspotContactSyncModalOpen, setHubspotContactSyncModalOpen] = useState(false);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [integrationToDisconnect, setIntegrationToDisconnect] = useState(null);
  const [lastSyncByIntegration, setLastSyncByIntegration] = useState({});
  const [activeZapierTab, setActiveZapierTab] = useState("crm");
  const [recipeModalOpen, setRecipeModalOpen] = useState(false);
  const [selectedRecipeIntegration, setSelectedRecipeIntegration] = useState(null);

  const formatLastSyncedLabel = (raw) => {
    if (!raw) return null;
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear();
    const dayNum = d.getDate();
    const ordinalDay = (n) => {
      const j = n % 10;
      const k = n % 100;
      if (j === 1 && k !== 11) return `${n}st`;
      if (j === 2 && k !== 12) return `${n}nd`;
      if (j === 3 && k !== 13) return `${n}rd`;
      return `${n}th`;
    };
    const timePart = d
      .toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
      .replace(/\s+/g, "")
      .toLowerCase();
    return `Last synced on ${month} ${ordinalDay(dayNum)}, ${year} @ ${timePart}`;
  };

  const zapierRecipeNamesByIntegrationId = {
    "zapier-paypal": [
      { name: "PayPal Payment Received", templateId: "255711427" },
      { name: "PayPal Payment Failed", templateId: "255711424" },
      { name: "PayPal Subscription Canceled", templateId: "255711423" },
    ],
    "zapier-stripe": [
      { name: "Stripe Payment Received", templateId: "255711414" },
      { name: "Stripe Payment Failed", templateId: "255711410" },
      { name: "Stripe Subscription Canceled", templateId: "255711411" },
    ],
    "zapier-gmail-engagement": [
      { name: "New Gmail email received", templateId: "255711428" },
    ],
    "zapier-outlook-engagement": [
      { name: "New Outlook email received", templateId: "255711437" },
    ],
  };

  const buildZapierRecipeUrl = (recipeName, templateId) => {
    return `${ZAPIER_WEBINTENT_BASE_URL}name=${encodeURIComponent(recipeName)}&template=${encodeURIComponent(templateId)}`;
  };

  const handleOpenRecipesModal = (integration) => {
    const recipes = zapierRecipeNamesByIntegrationId[integration.id] || [];
    const recipeLinks = recipes.map((recipe) => ({
      name: recipe.name,
      url: buildZapierRecipeUrl(recipe.name, recipe.templateId),
    }));

    setSelectedRecipeIntegration({
      title: integration.title,
      recipeLinks,
    });
    setRecipeModalOpen(true);
  };

  const getIntegrationRecipeCount = (integration) => {
    return (zapierRecipeNamesByIntegrationId[integration.id] || []).length;
  };

  const handleCloseRecipesModal = () => {
    setRecipeModalOpen(false);
    setSelectedRecipeIntegration(null);
  };

  const fetchIntegrationStatuses = async () => {
    setStatusLoading(true);
    setError(null);

    const workspaceId = Auth.getWorkspaceId();
    const user = Auth.getUser();
    const adminUserId = user?.id;

    try {
      const newStatus = { slack: false, "circle-so": false, highlevel: false, hubspot: false, zoom: false };
      const newLastSync = {};

      try {
        const slackResponse = await axios.get(`${API_URL}/slack/status`);
        Object.assign(newStatus, slackResponse.data);
        if (slackResponse.data?.slack && slackResponse.data?.lastSyncDate) {
          newLastSync.slack = slackResponse.data.lastSyncDate;
        }
      } catch (err) {
        console.error("Failed to fetch Slack integration status:", err);
        newStatus.slack = false;
      }

      if (workspaceId && adminUserId) {
        try {
          const circleResponse = await axios.get(
            `${API_URL}/circle/circle-token?workspaceId=${workspaceId}&adminUserId=${adminUserId}`
          );
          newStatus["circle-so"] = circleResponse.data.hasBotToken || false;
          if (circleResponse.data?.lastSyncDate) {
            newLastSync["circle-so"] = circleResponse.data.lastSyncDate;
          }
        } catch (err) {
          console.error("Failed to fetch Circle.so integration status:", err);
          newStatus["circle-so"] = false;
        }
      }

      try {
        // const API_URL = "http://localhost:4000"; // Replace with your actual API URL
        const ghlCBResponse = await axios.get(`${API_URL}/ghlcb/status`);

        newStatus.highlevel = ghlCBResponse.data.connected || false;
        if (ghlCBResponse.data?.connected === true && ghlCBResponse.data?.data?.updated_at) {
          newLastSync.highlevel = ghlCBResponse.data.data.updated_at;
        }
      } catch (err) {
        console.error("Failed to fetch HighLevel integration status:", err.message);
        newStatus.highlevel = false;
      }

      try {
        const hubspotResponse = await axios.get(`${API_URL}/hubspot/status`);
        newStatus.hubspot = hubspotResponse.data.connected || false;
        if (hubspotResponse.data?.connected === true && hubspotResponse.data?.data?.updated_at) {
          newLastSync.hubspot = hubspotResponse.data.data.updated_at;
        }
      } catch (err) {
        console.error("Failed to fetch HubSpot integration status:", err.message);
        newStatus.hubspot = false;
      }

      // Check Zoom integration status
      if (workspaceId && adminUserId) {
        try {
          const zoomResponse = await axios.get(
            `${API_URL}/zoom/zoom-has-integration?workspaceId=${workspaceId}`
          );
          newStatus.zoom = zoomResponse.data.hasIntegration || false;
          if (zoomResponse.data?.lastSyncDate) {
            newLastSync.zoom = zoomResponse.data.lastSyncDate;
          }
        } catch (err) {
          console.error("Failed to fetch Zoom integration status:", err);
          newStatus.zoom = false;
        }
      }

      setConnectionStatus(newStatus);
      setLastSyncByIntegration(newLastSync);

    } catch (err) {
      console.error("Error fetching integration statuses:", err);
      setError("Failed to fetch integration statuses");
      setConnectionStatus({ slack: false, "circle-so": false, highlevel: false, hubspot: false, zoom: false });
      setLastSyncByIntegration({});
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrationStatuses();
  }, []);

  // useEffect(() => {
  //   document.body.style.overflow = slackDialogOpen ? "hidden" : "auto";
  // }, [slackDialogOpen]);

  const connectableIntegrations = [
    {
      id: "slack",
      title: "Connect your Slack Channel",
      description:
        'Get instant notifications on your Slack channel. Click "Connect" to add a Slack channel.',
      logo: "/slack.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
    },
    {
      id: "circle-so",
      title: "Connect your Circle Community",
      description:
        "Sync real-time activity scores to your Client Retention Score reports and client dashboards.",
      logo: "/circle_so.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
    },
    {
      id: "highlevel",
      title: "Connect your HighLevel Account",
      description:
        "Sync your HighLevel CRM to seamlessly send client data to and from ClientBloom.",
      logo: "/high_level.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
      refreshText: "Refresh",
    },
    {
      id: "zoom",
      title: "Connect your Zoom Account",
      description:
        "Sync your Zoom account to seamlessly send call transcriptions to ClientBloom.",
      logo: "/Zoom_Logo_Bloom_RGB.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
    },
    {
      id: "hubspot",
      title: "Connect your HubSpot Account",
      description:
        "Sync your HubSpot CRM to seamlessly send client data to and from ClientBloom.",
      logo: "/hubspot.png",
      connectText: "Connect",
      disconnectText: "Disconnect",
      manageText: "Manage",
      refreshText: "Refresh",
    },
  ];

  const upcomingIntegrations = [
    {
      id: "gmail",
      title: "Connect your Gmail Account",
      description:
        "Use your Google email provider to send NPS and CES surveys to clients.",
      logo: "/gmail.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "outlook",
      title: "Connect your Outlook Account",
      description:
        "Use your Outlook email provider to send NPS and CES surveys to clients.",
      logo: "/outlook.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "active-campaign",
      title: "Connect your ActiveCampaign Account",
      description:
        "Sync your ActiveCampaign CRM to seamlessly send client data to and from ClientBloom.",
      logo: "/active_campaign.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "salesforce",
      title: "Connect your Salesforce Account",
      description:
        "Sync your Salesforce CRM to seamlessly send client data to and from ClientBloom.",
      logo: "/salesforce.png",
      connectText: "Connect",
      manageText: "Manage",
    },
    {
      id: "google-meet",
      title: "Connect your Google Meet Account",
      description:
        "Sync your Google Meet account to seamlessly send call transcriptions to ClientBloom.",
      logo: "/google_meet.png",
      connectText: "Connect",
      manageText: "Manage",
    },
  ];

  const zapierPaymentsIntegrations = [
    {
      id: "zapier-stripe",
      title: "Stripe",
      description: "Automate billing status updates and payment failure alerts from Stripe.",
      logo: "/stripe.png",
      recipes: 3,
    },
    {
      id: "zapier-paypal",
      title: "PayPal",
      description: "Sync PayPal payment events to keep billing status accurate and churn signals timely.",
      logo: "/paypal.png",
      recipes: 3,
    },
  ];

  const zapierOnboardingIntegrations = [
    {
      id: "zapier-notion",
      title: "Notion",
      description: "Track onboarding milestones from Notion project boards — contract signings, welcome calls, and more.",
      logo: "/notion.png",
      recipes: 3,
    },
    {
      id: "zapier-monday",
      title: "Monday.com",
      description: "Sync Monday.com project milestones to automate onboarding status tracking.",
      logo: "/monday.png",
      recipes: 2,
    },
    {
      id: "zapier-asana",
      title: "Asana",
      description: "Track Asana onboarding project completions and sync milestones to ClientBloom.",
      logo: "/asana.png",
      recipes: 1,
    },
    {
      id: "zapier-clickup",
      title: "ClickUp",
      description: "Sync ClickUp task completions to automate onboarding milestone tracking.",
      logo: "/clickup.png",
      recipes: 1,
    },
    {
      id: "zapier-google-sheets",
      title: "Google Sheets",
      description: "Track onboarding checklists managed in Google Sheets with automated status sync.",
      logo: "/google_sheets.png",
      recipes: 1,
    },
    {
      id: "zapier-pandadoc",
      title: "PandaDoc",
      description: "Auto-detect signed contracts and documents to trigger onboarding milestones.",
      logo: "/panda_doc.png",
      recipes: 1,
    },
    {
      id: "zapier-docusign",
      title: "DocuSign",
      description: "Sync signed envelopes to automatically advance onboarding and log contract events.",
      logo: "/docusign.png",
      recipes: 1,
    },
    {
      id: "zapier-calendly",
      title: "Calendly",
      description: "Sync scheduling events for onboarding calls, client meetings, and attendance tracking.",
      logo: "/calendly.png",
      recipes: 4,
    },
  ];

  const zapierEngagementIntegrations = [
    {
      id: "zapier-gmail-engagement",
      title: "Gmail",
      description: "Log client email communications to Interaction History with full subject and body content.",
      logo: "/gmail_logo.png",
      recipes: 2,
    },
    {
      id: "zapier-outlook-engagement",
      title: "Outlook",
      description: "Sync Outlook email activity to Interaction History for full communication intelligence.",
      logo: "/outlook_logo.png",
      recipes: 2,
    },
    {
      id: "zapier-google-meet-engagement",
      title: "Google Meet",
      description: "Capture call transcripts and attendance from Google Meet for CRS and BI Agent analysis.",
      logo: "/google_meet_logo.png",
      recipes: 2,
    },
    {
      id: "zapier-teams-engagement",
      title: "Microsoft Teams",
      description: "Sync Teams call transcripts and meeting data to ClientBloom Interaction History.",
      logo: "/microsoft_office_teams_logo.png",
      recipes: 1,
    },
    {
      id: "zapier-fathom-engagement",
      title: "Fathom",
      description: "Push Fathom AI meeting summaries and transcripts directly into Interaction History.",
      logo: "/fathom_logo.png",
      recipes: 1,
    },
    {
      id: "zapier-otter-engagement",
      title: "Otter.ai",
      description: "Sync Otter.ai transcriptions and meeting notes to Interaction History.",
      logo: "/otter_ai_logo.png",
      recipes: 1,
    },
    {
      id: "zapier-typeform-engagement",
      title: "Typeform",
      description: "Capture survey responses (NPS, CES) and form submissions with full field-level detail.",
      logo: "/typeform.png",
      recipes: 1,
    },
    {
      id: "zapier-jotform-engagement",
      title: "JotForm",
      description: "Sync JotForm submissions — onboarding forms, feedback surveys, and intake questionnaires.",
      logo: "/jotform.png",
      recipes: 1,
    },
    {
      id: "zapier-google-forms-engagement",
      title: "Google Forms",
      description: "Push Google Form responses to Interaction History for survey and intake tracking.",
      logo: "/google_forms_logo.png",
      recipes: 1,
    },
  ];

  const zapierCrmPlatformsIntegrations = [
    {
      id: "zapier-hubspot-crm",
      title: "HubSpot",
      description: "Bi-directional client data, email logs, and form submissions between HubSpot and ClientBloom.",
      logo: "/hubspot_logo.png",
      recipes: 3,
    },
    {
      id: "zapier-activecampaign-crm",
      title: "ActiveCampaign",
      description: "Sync ActiveCampaign contacts and email engagement to ClientBloom.",
      logo: "/active_campaign_logo.png",
      recipes: 2,
    },
    {
      id: "zapier-salesforce-crm",
      title: "Salesforce",
      description: "Enterprise CRM sync — opportunities, communications, and pipeline stages.",
      logo: "/salesforces_logo.png",
      recipes: 2,
    },
    {
      id: "zapier-clickfunnels-crm",
      title: "ClickFunnels 2.0",
      description: "Full membership site intelligence — subscriptions, course progress, community activity, and payments.",
      logo: "/click_funnels.png",
      recipes: 6,
    },
  ];


  const handleConnect = (id) => {
    setLoadingId(id);
    switch (id) {
      case "slack": {
        const workspaceId = Cookies.get("workspaceId");
        if (!workspaceId) {
          setError("Workspace ID not found in cookies");
          return;
        }
        const state = JSON.stringify({ workspaceId, csrf: generateCSRFToken() });
        window.location.href = `${API_URL}/slack/redirect?state=${encodeURIComponent(state)}`;
        break;
      }
      case "circle-so": {
        setCircleModalOpen(true);
        break;
      }
      case "highlevel":
        //const API_URL = "http://localhost:4000"; // Replace with your actual API URL
        const ghlWorkspaceId = Cookies.get("workspaceId");
        const connectState = JSON.stringify({ workspaceId: ghlWorkspaceId });
        const connectUrl = `${API_URL}/ghlcb/connect?state=${encodeURIComponent(connectState)}`;


        window.location.href = connectUrl;
        break;
      case "hubspot": {
        const hsWorkspaceId = Cookies.get("workspaceId");
        const hsConnectState = JSON.stringify({ workspaceId: hsWorkspaceId });
        window.location.href = `${API_URL}/hubspot/connect?state=${encodeURIComponent(hsConnectState)}`;
        break;
      }
      case "zoom": {
        // Zoom connect: Direct navigation to the backend so the browser follows the 302
        const workspaceId = Auth.getWorkspaceId();
        const user = Auth.getUser();
        const userId = user?.id;
        if (!workspaceId || !userId) {
          setError("Workspace ID or User ID not found");
          return;
        }
  // Direct navigation is required so the browser can follow the OAuth 302 redirect for Zoom authentication.
  window.location.href = `${API_URL}/zoom/zoom-connect?workspaceId=${workspaceId}&userId=${userId}`;
        break;
      }
      default:
        console.log(`Connect clicked for ${id}`);
        break;
    }
  };

  const handleDisconnect = async (id) => {
    // For Zoom, show confirmation dialog first
    if (id === "zoom") {
      setIntegrationToDisconnect(id);
      setConfirmDisconnectOpen(true);
      return;
    }

    // For other integrations, proceed directly
    await performDisconnect(id);
  };

  const performDisconnect = async (id) => {
    setDisconnectLoading(true);
    setLoadingId(id);
    try {
      switch (id) {
        case "slack":
          await axios
            .post(
              `${API_URL}/slack/disconnect`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${Auth.getToken()}`,
                },
              }
            ).then(() => {
              setConnectionStatus((prev) => ({ ...prev, slack: false }));
              toast.success('Slack integration disconnected successfully!');
            })
            .catch((err) => toast.error(`Failed to disconnect Slack. Error: ${err}`))
            .finally(() => {
              setDisconnectLoading(false);
            });
          break;
        case "circle-so":
          const workspaceId = Auth.getWorkspaceId();
          const user = Auth.getUser();
          const adminUserId = user?.id;
          if (!workspaceId || !adminUserId) {
            setError("Workspace ID or Admin User ID not found");
            return;
          }
          await axios.delete(
            `${API_URL}/circle/circle-token?workspaceId=${workspaceId}&adminUserId=${adminUserId}`,
            {
              headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
              },
            }
          );
          await fetchIntegrationStatuses();
          break;
        case "highlevel":
          //const API_URL = "http://localhost:4000"; // Replace with your actual API URL

          await axios
            .post(
              `${API_URL}/ghlcb/disconnect`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${Auth.getToken()}`,
                },
              }
            ).then((response) => {
              setConnectionStatus((prev) => ({ ...prev, highlevel: false }));
              toast.success('HighLevel integration disconnected successfully!');
            })
            .catch((err) => {
              toast.error(`Failed to disconnect HighLevel. Error: ${err}`);
            })
            .finally(() => {
              setDisconnectLoading(false);
            });
          break;
        case "hubspot":
          await axios
            .post(
              `${API_URL}/hubspot/disconnect`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${Auth.getToken()}`,
                },
              }
            ).then(() => {
              setConnectionStatus((prev) => ({ ...prev, hubspot: false }));
              toast.success('HubSpot integration disconnected successfully!');
            })
            .catch((err) => {
              toast.error(`Failed to disconnect HubSpot. Error: ${err}`);
            })
            .finally(() => {
              setDisconnectLoading(false);
            });
          break;
        case "zoom":
          const zoomWorkspaceId = Auth.getWorkspaceId();
          const zoomUser = Auth.getUser();
          const zoomUserId = zoomUser?.id;
          if (!zoomWorkspaceId || !zoomUserId) {
            setError("Workspace ID or User ID not found");
            return;
          }
          await axios.delete(
            `${API_URL}/zoom/zoom-disconnect?workspaceId=${zoomWorkspaceId}&userId=${zoomUserId}`,
            {
              headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
              },
            }
          );
          await fetchIntegrationStatuses();
          toast.success('Zoom integration disconnected successfully!');
          break;
        default:
            console.log(`Disconnect clicked for ${id}`);
          break;
      }
    } catch (err) {
      setError(`Failed to disconnect ${id}`);
      console.error(err);
    } finally {
      setDisconnectLoading(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    setConfirmDisconnectOpen(false);
    if (integrationToDisconnect) {
      await performDisconnect(integrationToDisconnect);
      setIntegrationToDisconnect(null);
    }
  };

  const handleCancelDisconnect = () => {
    setConfirmDisconnectOpen(false);
    setIntegrationToDisconnect(null);
  };

  const handleRefresh = async (id) => {
    setManageLoading(true);
    setLoadingId(id);
    try {
      switch (id) {
        case "highlevel":
          // const API_URL = "http://localhost:4000"; // Replace with your actual API URL

          await axios
            .get(
              `${API_URL}/ghlcb/refresh`,
              {
                headers: {
                  Authorization: `Bearer ${Auth.getToken()}`,
                },
              }
            ).then((response) => {
              toast.success('HighLevel integration refreshed successfully!');
              setConnectionStatus((prev) => ({ ...prev, highlevel: true }));
            })
            .catch((err) => {
              toast.error(`Failed to refresh HighLevel. Error: ${err}`);
              setConnectionStatus((prev) => ({ ...prev, highlevel: "expired" }));
            })
            .finally(() => {
              setManageLoading(false);
            });
          break;
        case "hubspot":
          await axios
            .get(
              `${API_URL}/hubspot/refresh`,
              {
                headers: {
                  Authorization: `Bearer ${Auth.getToken()}`,
                },
              }
            ).then(() => {
              toast.success('HubSpot integration refreshed successfully!');
              setConnectionStatus((prev) => ({ ...prev, hubspot: true }));
            })
            .catch((err) => {
              toast.error(`Failed to refresh HubSpot. Error: ${err}`);
              setConnectionStatus((prev) => ({ ...prev, hubspot: "expired" }));
            })
            .finally(() => {
              setManageLoading(false);
            });
          break;
        default:
            console.log(`Refresh clicked for ${id}`);
          break;
      }
    } catch (err) {
      setError(`Failed to refresh ${id}`);
      console.error(err);
    } finally {
      setManageLoading(false);
    }
  };

  const handleManage = (id) => {
    setManageLoading(true);
    setLoadingId(id);
    switch (id) {
      case "slack":
        if (!connectionStatus.slack) {
          setError("Slack is not connected");
          return;
        }
        setSlackModalOpen(true);
        setManageLoading(false);
        break;
      case "circle-so":
        if (!connectionStatus["circle-so"]) {
          setError("Circle.so is not connected");
          return;
        }
        setCircleManagementOpen(true);
        setManageLoading(false);
        break;
      case "highlevel":
        if (!connectionStatus.highlevel) {
          setError("HighLevel is not connected");
          return;
        }
        setHighLevelModalOpen(true);
        setManageLoading(false);
        break;
      case "hubspot":
        if (!connectionStatus.hubspot) {
          setError("HubSpot is not connected");
          return;
        }
        setHubspotModalOpen(true);
        setManageLoading(false);
        break;
      case "zoom":
        if (!connectionStatus.zoom) {
          setError("Zoom is not connected");
          return;
        }
        setZoomManagementOpen(true);
        setManageLoading(false);
        break;
      default:
        setManageLoading(false);
    }
  };

  const handleCircleModalClose = () => {
    setCircleModalOpen(false);
  };

  const handleCircleModalSuccess = async () => {
    await fetchIntegrationStatuses();
  };

  const handleCircleManagementBack = () => {
    setCircleManagementOpen(false);
  };

  const handleZoomManagementBack = () => {
    setZoomManagementOpen(false);
  };

  const zapierTabDefs = [
    { id: "crm", label: "CRM & Platforms", icon: "🗂️", list: zapierCrmPlatformsIntegrations },
    { id: "engagement", label: "Engagement", icon: "💬", list: zapierEngagementIntegrations },
    { id: "onboarding", label: "Onboarding", icon: "🚀", list: zapierOnboardingIntegrations },
    { id: "payments", label: "Payments", icon: "💳", list: zapierPaymentsIntegrations },
  ];

  const activeZapierList =
    zapierTabDefs.find((t) => t.id === activeZapierTab)?.list || zapierCrmPlatformsIntegrations;

  const renderZapierCard = (integration) => {
    const recipeCount = getIntegrationRecipeCount(integration);
    return (
      <div key={integration.id} className="zapier-card">
        <div className="zapier-card-corner-bolt">⚡</div>
        <div className="zapier-logos">
          <img src={integration.logo} alt={integration.title} className="zapier-logo" />
          <span className="zapier-plus">+</span>
          <img src="/cb_logo.png" alt="ClientBloom" className="zapier-logo" />
        </div>
        <h3>{integration.title}</h3>
        <p>{integration.description}</p>
        <div className="zapier-recipe-count">
          <span className="zapier-recipe-number">{recipeCount}</span>
          <span className="zapier-recipe-label">{recipeCount === 1 ? "recipe" : "recipes"}</span>
        </div>
        <Button
          className="zapier-recipes-btn"
          variant="contained"
          size="small"
          onClick={() => handleOpenRecipesModal(integration)}
        >
          View Recipes
        </Button>
      </div>
    );
  };

  return (
    <>
      {error && <div className="error-message">{error}</div>}
      <div className="integrations-main">
        <div className="integrations-grid">
          {connectableIntegrations.map((integration) => {
            const showNativeLastSync =
              ["slack", "circle-so", "highlevel", "hubspot", "zoom"].includes(integration.id) &&
              !!connectionStatus[integration.id];
            const nativeLastSyncLine = showNativeLastSync
              ? formatLastSyncedLabel(lastSyncByIntegration[integration.id]) ||
                "Connected — last sync time not available yet."
              : null;

            return (
            <div key={integration.id} className="integration-cell">
            <div className="integration-card">
              <div className="new-badge">NEW</div>
              <h2>{integration.title}</h2>
              <img
                src={integration.logo}
                alt={integration.title}
                className="integration-logo"
              />
              <p>{integration.description}</p>
              <div className="button-group-vertical">
                {connectionStatus[integration.id] ? (
                  <>
                    <Box
                      sx={{ m: 1, position: 'relative' }}>
                      <Button
                        className="disconnect-button"
                        sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                        onClick={() => handleDisconnect(integration.id)}
                        variant="outlined"
                        size="large"
                        color="error"
                        disabled={disconnectLoading && loadingId === integration.id}
                      >
                        {integration.disconnectText}
                      </Button>
                      {disconnectLoading && loadingId === integration.id && (
                        <CircularProgress
                          size={24}
                          sx={{
                            color: colors.red[500],
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                          }}
                        />
                      )}
                    </Box>
                    {integration.id !== "zapier" && connectionStatus[integration.id] === "expired" ? (
                      <Box
                        sx={{ m: 1, position: 'relative' }}>
                        <Button
                          className="refresh-button"
                          sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                          onClick={() => handleRefresh(integration.id)}
                          variant="outlined"
                          color="success"
                          size="large"
                          disabled={manageLoading && loadingId === integration.id}
                        >
                          {integration.refreshText}
                        </Button>
                        {manageLoading && loadingId === integration.id && (
                          <CircularProgress
                            size={24}
                            sx={{
                              color: colors.green[500],
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: '-12px',
                              marginLeft: '-12px',
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{ m: 1, position: 'relative' }}>
                        <Button
                          className="manage-button"
                          sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                          onClick={() => handleManage(integration.id)}
                          variant="outlined"
                          color="success"
                          size="large"
                          disabled={manageLoading && loadingId === integration.id}
                        >
                          {integration.manageText}
                        </Button>
                        {manageLoading && loadingId === integration.id && (
                          <CircularProgress
                            size={24}
                            sx={{
                              color: colors.green[500],
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: '-12px',
                              marginLeft: '-12px',
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Box
                      sx={{ m: 1, position: 'relative' }}>
                      <Button
                        className="connect-button"
                        sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                        onClick={() => handleConnect(integration.id)}
                        variant="outlined"
                        color="primary"
                        size="large"
                        disabled={statusLoading}
                      >
                        {integration.connectText}
                      </Button>
                      {statusLoading && (
                        <CircularProgress
                          size={24}
                          sx={{
                            color: colors.blue[500],
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                          }}
                        />
                      )}
                    </Box>
                    {integration.id === "zapier" && !statusLoading && (
                      <Box
                        sx={{ m: 1, position: 'relative' }}>
                        <Button
                          className="manage-button"
                          sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                          onClick={() => handleManage(integration.id)}
                          variant="outlined"
                          color="success"
                          size="large"
                          disabled={manageLoading && loadingId === integration.id}
                        >
                          {integration.manageText}
                        </Button>
                        {manageLoading && loadingId === integration.id && (
                          <CircularProgress
                            size={24}
                            sx={{
                              color: colors.green[500],
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: '-12px',
                              marginLeft: '-12px',
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </>
                )}
              </div>
            </div>
            {showNativeLastSync && (
              <p className="integration-last-sync" title={nativeLastSyncLine}>
                {nativeLastSyncLine}
              </p>
            )}
            </div>
            );
          })}
        </div>

        <div className="zapier-section">
          <div className="zapier-section-header">
            <h2>Zapier-Powered Integrations</h2>
            <span className="zapier-badge"><span className="zapier-badge-bolt">⚡</span>VIA ZAPIER</span>
          </div>
          <p className="zapier-section-subtitle">
            Pre-built recipes designed to feed your Client Retention Score and BI Agent automatically. Click any platform to view and activate.
          </p>

          <div className="zapier-tabs" role="tablist" aria-label="Zapier integration categories">
            {zapierTabDefs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeZapierTab === tab.id}
                className={`zapier-tab ${activeZapierTab === tab.id ? "zapier-tab-active" : ""}`}
                onClick={() => setActiveZapierTab(tab.id)}
              >
                <span className="zapier-tab-icon" aria-hidden="true">
                  {tab.icon}
                </span>
                <span className="zapier-tab-label">{tab.label}</span>
                <span className="zapier-tab-count">({tab.list.length})</span>
              </button>
            ))}
          </div>

          <div className="zapier-grid zapier-grid-tabbed" role="tabpanel">
            {activeZapierList.map((integration) => renderZapierCard(integration))}
          </div>
        </div>

        <div className="bannerSoon">These integrations are coming soon.</div>

        <div className="integrations-grid">{upcomingIntegrations.map((integration) => (
          <div key={integration.id} className="integration-card">
            <h2>{integration.title}</h2>
            <img
              src={integration.logo}
              alt={integration.title}
              className="integration-logo"
            />
            <p>{integration.description}</p>
            <div className="button-group-vertical">
              {connectionStatus[integration.id] ? (
                <>
                  <Box
                    sx={{ m: 1, position: 'relative' }}>
                    <Button
                      className="disconnect-button"
                      sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                      onClick={() => handleDisconnect(integration.id)}
                      variant="outlined"
                      size="large"
                      color="error"
                      disabled={disconnectLoading && loadingId === integration.id}
                    >
                      {integration.disconnectText}
                    </Button>
                    {disconnectLoading && loadingId === integration.id && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: colors.red[500],
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-12px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{ m: 1, position: 'relative' }}>
                    <Button
                      className="manage-button"
                      sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                      onClick={() => handleManage(integration.id)}
                      variant="outlined"
                      color="success"
                      size="large"
                      disabled={manageLoading && loadingId === integration.id}
                    >
                      {integration.manageText}
                    </Button>
                    {manageLoading && loadingId === integration.id && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: colors.green[500],
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-12px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <Box
                  sx={{ m: 1, position: 'relative' }}>
                  <Button
                    className="connect-button"
                    sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
                    onClick={() => handleConnect(integration.id)}
                    variant="outlined"
                    color="primary"
                    size="large"
                    disabled={statusLoading}
                  >
                    {integration.connectText}
                  </Button>
                  {statusLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: colors.blue[500],
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Box>
              )}
            </div>
          </div>
        ))}
        </div>

      </div >
      {slackModalOpen && <SlackModal onClose={() => setSlackModalOpen(false)} />}
      <CircleModal
        isOpen={circleModalOpen}
        onClose={handleCircleModalClose}
        onSuccess={handleCircleModalSuccess}
        isAlreadyConnected={connectionStatus["circle-so"]}
      />
      {circleManagementOpen && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '90vw', width: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <CircleManagement onBack={handleCircleManagementBack} />
          </div>
        </div>
      )}
      {zoomManagementOpen && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '90vw', width: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <ZoomManagement onBack={handleZoomManagementBack} />
          </div>
        </div>
      )}
      {highLevelModalOpen && <HighLevelModal onClose={(event) => {
        setHighLevelModalOpen(false);
        if (event === "manualSync") setHighLevelContactSyncModalOpen(true);
      }} />}
      {highLevelContactSyncModealOpen && <HighLevelContactSync onClose={(event) => {
        setHighLevelContactSyncModalOpen(false);
        if (event === 'refreshStats' && highLevelModalOpen) {
          setTimeout(() => {
            const refreshEvent = new CustomEvent('refreshHighLevelStats');
            window.dispatchEvent(refreshEvent);
          }, 500);
        }
      }} />}

      {hubspotModalOpen && <HubspotModal onClose={(event) => {
        setHubspotModalOpen(false);
        if (event === "manualSync") setHubspotContactSyncModalOpen(true);
      }} />}
      {hubspotContactSyncModalOpen && <HubspotContactSync onClose={(event) => {
        setHubspotContactSyncModalOpen(false);
        if (event === 'refreshStats' && hubspotModalOpen) {
          setTimeout(() => {
            const refreshEvent = new CustomEvent('refreshHubspotStats');
            window.dispatchEvent(refreshEvent);
          }, 500);
        }
      }} />}

      {/* Zoom Disconnect Confirmation Dialog */}
      <Dialog
        open={confirmDisconnectOpen}
        onClose={handleCancelDisconnect}
        aria-labelledby="disconnect-dialog-title"
        aria-describedby="disconnect-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '0.5rem',
          }
        }}
      >
        <DialogTitle 
          id="disconnect-dialog-title"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1a202c',
            paddingBottom: '0.5rem'
          }}
        >
          Disconnect Zoom Integration
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '1rem' }}>
          <DialogContentText 
            id="disconnect-dialog-description"
            sx={{
              color: '#4a5568',
              fontSize: '1rem',
              lineHeight: 1.7,
              marginBottom: '1rem'
            }}
          >
            Are you sure you want to disconnect the Zoom integration?
          </DialogContentText>
          
          <Box
            sx={{
              backgroundColor: '#fff5f5',
              border: '1px solid #feb2b2',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem'
            }}
          >
            <Box sx={{ fontWeight: 600, color: '#c53030', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
              This action will:
            </Box>
            <Box component="ul" sx={{ margin: 0, paddingLeft: '1.5rem', color: '#4a5568' }}>
              <li style={{ marginBottom: '0.5rem' }}>Stop syncing new recordings from Zoom</li>
              <li style={{ marginBottom: '0.5rem' }}>Revoke ClientBloom's access to your Zoom account</li>
              <li>Remove the OAuth connection tokens</li>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: '#ebf8ff',
              border: '1px solid #90cdf4',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start'
            }}
          >
            <Box sx={{ fontSize: '1.25rem', flexShrink: 0 }}>ℹ️</Box>
            <Box sx={{ color: '#2c5282', fontSize: '0.9rem', lineHeight: 1.6 }}>
              <strong>Note:</strong> Previously synced calls and transcripts will remain in ClientBloom.
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '1rem 1.5rem', gap: '0.75rem' }}>
          <Button 
            onClick={handleCancelDisconnect}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              padding: '0.625rem 1.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderWidth: '2px',
              '&:hover': {
                borderWidth: '2px'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDisconnect} 
            variant="contained"
            color="error"
            autoFocus
            sx={{
              borderRadius: '8px',
              padding: '0.625rem 1.75rem',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #f56565 0%, #c53030 100%)',
              boxShadow: '0 4px 12px rgba(197, 48, 48, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e53e3e 0%, #9b2c2c 100%)',
                boxShadow: '0 6px 16px rgba(197, 48, 48, 0.4)',
              }
            }}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
      {recipeModalOpen && (
        <div className="dialog-overlay" onClick={handleCloseRecipesModal}>
          <div
            className="dialog recipe-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="recipe-modal-header">
              <div>
                <h2 className="recipe-modal-title">{selectedRecipeIntegration?.title} Recipes</h2>
                <p className="recipe-modal-subtitle">Choose a recipe to open in Zapier.</p>
              </div>
              <Button className="recipe-modal-close-icon" onClick={handleCloseRecipesModal}>×</Button>
            </div>

            <div className="recipe-modal-count">
              {(selectedRecipeIntegration?.recipeLinks?.length || 0)} {(selectedRecipeIntegration?.recipeLinks?.length || 0) === 1 ? "recipe" : "recipes"}
            </div>

            {(selectedRecipeIntegration?.recipeLinks?.length || 0) > 0 ? (
              <div className="recipe-modal-links">
                {selectedRecipeIntegration.recipeLinks.map((recipeLink) => (
                  <a
                    key={recipeLink.name}
                    href={recipeLink.url}
                    target="_blank"
                    rel="noreferrer"
                    className="recipe-modal-link"
                  >
                    <span>{recipeLink.name}</span>
                  </a>
                ))}
              </div>
            ) : (
              <div className="recipe-modal-empty">
                <p>No recipes available yet for this integration.</p>
              </div>
            )}

            <div className="recipe-modal-footer">
              <Button className="recipe-modal-close-btn" variant="outlined" onClick={handleCloseRecipesModal}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IntegrationsMain;
